import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHotel } from "../../contexts/HotelContext";
import { PlatformHotelSearchResult } from "../../services/admin/types/account.types";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ArrowLeftRight, Building2, Check, ChevronDown, Crown, Plus, Search, Shield } from "lucide-react";
import { cn } from "../../lib/utils";

const HotelSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    myHotelInfo,
    availableHotels,
    hasMultipleHotels,
    isPlatformAdmin,
    registrationStatus,
    selectHotel,
    searchAllHotels,
    isSwitching,
  } = useHotel();

  const currentHotelId = myHotelInfo?.hotelId;
  const currentHotel = availableHotels.find((h) => h.hotelId === currentHotelId);

  if (!myHotelInfo && availableHotels.length === 0 && !isPlatformAdmin) {
    return null;
  }

  if (isPlatformAdmin) {
    return (
      <PlatformAdminSwitcher
        currentHotelId={currentHotelId}
        currentHotelName={myHotelInfo?.hotelName ?? null}
        isSwitching={isSwitching}
        selectHotel={selectHotel}
        searchAllHotels={searchAllHotels}
      />
    );
  }

  return (
    <RegularHotelSwitcher
      currentHotelId={currentHotelId}
      currentHotel={currentHotel}
      myHotelInfo={myHotelInfo}
      availableHotels={availableHotels}
      hasMultipleHotels={hasMultipleHotels}
      registrationStatus={registrationStatus}
      isSwitching={isSwitching}
      selectHotel={selectHotel}
      navigate={navigate}
      t={t}
    />
  );
};

interface PlatformAdminSwitcherProps {
  currentHotelId: string | undefined;
  currentHotelName: string | null;
  isSwitching: boolean;
  selectHotel: (hotelId: string) => Promise<void>;
  searchAllHotels: (search?: string) => Promise<PlatformHotelSearchResult[]>;
}

const PlatformAdminSwitcher: React.FC<PlatformAdminSwitcherProps> = ({
  currentHotelId,
  currentHotelName,
  isSwitching,
  selectHotel,
  searchAllHotels,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<PlatformHotelSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(
    async (query: string): Promise<void> => {
      setIsSearching(true);
      const results = await searchAllHotels(query);
      setSearchResults(results);
      setIsSearching(false);
    },
    [searchAllHotels],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const query = e.target.value;
      setSearchQuery(query);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(query);
      }, 300);
    },
    [handleSearch],
  );

  const handleSelectHotel = async (hotelId: string): Promise<void> => {
    if (hotelId !== currentHotelId && !isSwitching) {
      setIsOpen(false);
      await selectHotel(hotelId);
    }
  };

  useEffect(() => {
    if (isOpen) {
      handleSearch("");
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [isOpen, handleSearch]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 md:max-w-[250px] border-amber-500/50"
          disabled={isSwitching}
        >
          <Shield className="h-4 w-4 shrink-0 text-amber-500" />
          <span className="hidden md:inline truncate">
            {currentHotelName || t("platform_admin_select_hotel", "Select Hotel")}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50 hidden md:inline" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <Shield className="h-4 w-4" />
          {t("platform_admin_hotel_search", "Support Access")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 pb-2" onKeyDown={(e) => e.stopPropagation()}>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t("platform_admin_search_placeholder", "Search hotels...")}
              className="w-full rounded-md border border-input bg-background px-8 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {isSearching && (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              {t("common_searching", "Searching...")}
            </div>
          )}
          {!isSearching && searchResults.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              {searchQuery
                ? t("platform_admin_no_results", "No hotels found")
                : t("platform_admin_type_to_search", "Type to search hotels")}
            </div>
          )}
          {!isSearching &&
            searchResults.map((hotel) => {
              const isSelected = hotel.hotelId === currentHotelId;
              return (
                <DropdownMenuItem
                  key={hotel.hotelId}
                  onClick={() => handleSelectHotel(hotel.hotelId)}
                  className={cn(
                    "flex items-center gap-3 cursor-pointer",
                    isSelected && "bg-accent",
                  )}
                  disabled={isSwitching}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{hotel.hotelName}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {hotel.roomCount} {t("platform_admin_rooms", "rooms")}
                      {hotel.billingModel && ` · ${hotel.billingModel}`}
                      {hotel.countryCode && ` · ${hotel.countryCode}`}
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface RegularHotelSwitcherProps {
  currentHotelId: string | undefined;
  currentHotel: { hotelName: string } | undefined;
  myHotelInfo: { hotelName?: string | null } | null;
  availableHotels: Array<{
    hotelId: string;
    hotelName: string;
    isOwner: boolean;
    roleName?: string | null;
  }>;
  hasMultipleHotels: boolean;
  registrationStatus: { canRegister: boolean; totalHotels: number; maxHotels: number } | null;
  isSwitching: boolean;
  selectHotel: (hotelId: string) => Promise<void>;
  navigate: (path: string) => void;
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string;
}

const RegularHotelSwitcher: React.FC<RegularHotelSwitcherProps> = ({
  currentHotelId,
  currentHotel,
  myHotelInfo,
  availableHotels,
  hasMultipleHotels,
  registrationStatus,
  isSwitching,
  selectHotel,
  navigate,
  t,
}) => {
  const canAddHotel = registrationStatus?.canRegister ?? false;
  const totalHotels = registrationStatus?.totalHotels ?? availableHotels.length;
  const maxHotels = registrationStatus?.maxHotels ?? 5;

  const handleSelectHotel = async (hotelId: string): Promise<void> => {
    if (hotelId !== currentHotelId && !isSwitching) {
      await selectHotel(hotelId);
    }
  };

  const handleAddHotel = (): void => {
    navigate("/admin/register-hotel");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="hotel-switcher-trigger"
          variant="outline"
          className="flex items-center gap-2 md:max-w-[200px]"
          disabled={isSwitching}
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="hidden md:inline truncate" data-testid="hotel-switcher-current-name">
            {currentHotel?.hotelName || myHotelInfo?.hotelName || t("hotel_switcher_select", "Select Hotel")}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50 hidden md:inline" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="hotel-switcher-menu">
        {hasMultipleHotels && (
          <>
            <DropdownMenuLabel className="flex items-center gap-2">
              <ArrowLeftRight className="h-4 w-4" />
              {t("hotel_switcher_title", "Switch Hotel")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {availableHotels.map((hotel) => {
          const isSelected = hotel.hotelId === currentHotelId;
          return (
            <DropdownMenuItem
              key={hotel.hotelId}
              data-testid="hotel-switcher-option"
              data-hotel-id={hotel.hotelId}
              data-hotel-name={hotel.hotelName}
              onClick={() => handleSelectHotel(hotel.hotelId)}
              className={cn(
                "flex items-center gap-3 cursor-pointer",
                isSelected && "bg-accent"
              )}
              disabled={isSwitching}
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-medium truncate">{hotel.hotelName}</span>
                  {hotel.isOwner && (
                    <Crown className="h-3 w-3 text-yellow-500 shrink-0" />
                  )}
                </div>
                {hotel.roleName && (
                  <span className="text-xs text-muted-foreground truncate">
                    {hotel.roleName}
                  </span>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleAddHotel}
          disabled={isSwitching}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <Plus className="h-4 w-4 text-primary" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-medium">
              {t("hotel_switcher_add_hotel", "Add New Hotel")}
            </span>
            {!canAddHotel && totalHotels >= maxHotels ? (
              <span className="text-xs text-muted-foreground">
                {t("hotel_switcher_limit_reached", "Limit reached ({{count}}/{{max}})", { count: totalHotels, max: maxHotels })}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("hotel_switcher_hotel_count", "{{count}} of {{max}} hotels", { count: totalHotels, max: maxHotels })}
              </span>
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HotelSwitcher;
